import React, { useState, useEffect, useCallback } from "react";
import { Tabs, Card, message, Modal, Input, Button } from "antd";
import orderService from "../../services/OrderLists";
import usePermissions from "../../hooks/usePermissions";
import WordCountRequestFilters from "./WordCountRequestFilters";
import WordCountRequestsTable from "./WordCountRequestsTable";

const { TabPane } = Tabs;
const { confirm } = Modal;

const WordCountRequests = () => {
  const { hasPermission } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    total: 0,
  });
  const [filters, setFilters] = useState({});
  const [filtersList, setFiltersList] = useState([]);

  const canViewRaised = hasPermission(
    "can_view_extra_word_count_raised_request",
  );
  const canViewPending = hasPermission(
    "can_view_extra_word_count_pending_request",
  );

  useEffect(() => {
    if (activeTab === null) {
      if (canViewRaised) {
        setActiveTab("my");
      } else if (canViewPending) {
        setActiveTab("all");
      }
    }
  }, [canViewRaised, canViewPending, activeTab]);

  const fetchData = useCallback(
    async (type, page = 1, pageSize = 15, currentFilters = {}) => {
      if (!type) return;
      setLoading(true);
      try {
        const response =
          type === "my"
            ? await orderService.getManagerExtraWordCountRequests(
                page,
                pageSize,
                currentFilters,
              )
            : await orderService.getBDMExtraWordCountRequests(
                page,
                pageSize,
                currentFilters,
              );

        if (response.success) {
          // Handle different possible structures (straight from user's JSON)
          const apiData = response.data?.data || response.data;
          const results = apiData?.results || [];
          const count = apiData?.count || 0;
          const availableFilters = apiData?.filters || [];

          setData(results);
          setPagination((prev) => ({
            ...prev,
            current: page,
            pageSize: pageSize,
            total: count,
          }));
          setFiltersList(availableFilters);
        } else {
          message.error(
            `Failed to fetch ${type === "my" ? "my" : "all"} requests`,
          );
        }
      } catch (error) {
        console.error(`Error fetching ${type} requests:`, error);
        message.error(`An error occurred while fetching ${type} requests`);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (activeTab) {
      fetchData(activeTab, pagination.current, pagination.pageSize, filters);
    }
  }, [activeTab, pagination.current, pagination.pageSize, filters, fetchData]);

  const handleSearch = (newFilters) => {
    setFilters(newFilters);
    setData([]); // Clear current data to show loading state more clearly
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleDownloadCSV = async () => {
    if (!activeTab) return;
    try {
      const response =
        activeTab === "my"
          ? await orderService.downloadManagerExtraWordCountCSV(
              pagination.current,
              pagination.pageSize,
              filters,
            )
          : await orderService.downloadBDMExtraWordCountCSV(
              pagination.current,
              pagination.pageSize,
              filters,
            );

      if (!response?.success) {
        message.error("Failed to download CSV");
      }
    } catch (error) {
      console.error("Error downloading CSV:", error);
      message.error("An error occurred while downloading CSV");
    }
  };

  const handleAction = async (id, action, rejection_reason) => {
    try {
      const response = await orderService.extraWordCountAction(
        id,
        action,
        rejection_reason,
      );
      if (response.success) {
        message.success(`Request ${action.toLowerCase()} successfully`);
        fetchData(activeTab, pagination.current, pagination.pageSize, filters);
      } else {
        message.error(`Failed to ${action.toLowerCase()} request`);
      }
    } catch (error) {
      console.error(`Error during ${action} action:`, error);
      message.error(
        `An error occurred while ${action.toLowerCase()}ing the request`,
      );
    }
  };

  const showConfirm = (id, action) => {
    let rejectionReason = "";
    confirm({
      title: `Are you sure you want to ${action.toLowerCase()} this request?`,
      icon: null,
      content: (
        <div>
          <p>This action will mark the request as {action.toLowerCase()}.</p>
          {action === "REJECTED" && (
            <div style={{ marginTop: "10px" }}>
              <div style={{ marginBottom: "5px" }}>
                <strong>Rejection Reason:</strong>
              </div>
              <Input.TextArea
                rows={4}
                placeholder="Enter the reason for rejection"
                onChange={(e) => (rejectionReason = e.target.value)}
              />
            </div>
          )}
        </div>
      ),
      okText: "Confirm",
      okType: action === "APPROVED" ? "primary" : "danger",
      cancelText: "Cancel",
      async onOk() {
        if (action === "REJECTED" && !rejectionReason.trim()) {
          message.error("Please provide a rejection reason");
          return Promise.reject();
        }
        await handleAction(
          id,
          action,
          action === "REJECTED" ? rejectionReason : undefined,
        );
      },
      onCancel() {
        console.log("Cancel");
      },
    });
  };

  return (
    <div style={{ padding: "20px" }}>
      <WordCountRequestFilters
        onSearch={handleSearch}
        onReset={() => handleSearch({})}
        filtersList={filtersList}
        loading={loading}
        filters={filters}
      />
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: "10px",
        }}
      >
        <Button
          type="primary"
          style={{ backgroundColor: "#040724" }}
          onClick={handleDownloadCSV}
          disabled={!activeTab}
        >
          Download CSV
        </Button>
      </div>
      <Tabs
        activeKey={activeTab}
        onChange={(key) => {
          setActiveTab(key);
          setPagination((prev) => ({ ...prev, current: 1 }));
          setFilters({});
        }}
      >
        {hasPermission("can_view_extra_word_count_raised_request") && (
          <TabPane tab="My Requests" key="my">
            <Card bodyStyle={{ padding: 0 }}>
              <WordCountRequestsTable
                data={data}
                loading={loading}
                pagination={pagination}
                setPagination={setPagination}
                activeTab={activeTab}
                showConfirm={showConfirm}
              />
            </Card>
          </TabPane>
        )}
        {hasPermission("can_view_extra_word_count_pending_request") && (
          <TabPane tab="All Requests" key="all">
            <Card bodyStyle={{ padding: 0 }}>
              <WordCountRequestsTable
                data={data}
                loading={loading}
                pagination={pagination}
                setPagination={setPagination}
                activeTab={activeTab}
                showConfirm={showConfirm}
              />
            </Card>
          </TabPane>
        )}
      </Tabs>
    </div>
  );
};

export default WordCountRequests;
